import { useContext, useEffect, useState } from 'react';

import { ONE, TWO } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Alert, Snackbar, Typography } from '@/ui';
import { track } from '@/utils';

import OptionCard from '@/components/GetStarted/Matching/OptionsCard';
import { GetStartedContext } from '@/components/GetStarted/context';
import {
  useUpdateUserAssessmentMutation,
  useUpdateUserProfileMutation,
} from '@/store/api/userApi';
import { captureException } from '@/utils/errorMonitoring';

const GetCoach = () => {
  const { user, refetchUser } = useCurrentUser();
  const { nextStep } = useContext(GetStartedContext);
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateUserAssessment] = useUpdateUserAssessmentMutation();

  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    track('ob_match');
  }, []);

  const onNext = async (option: string) => {
    try {
      setSaving(true);
      setErrorMessage(null);

      await updateUserProfile({
        id: user.id,
        userProfile: { matchPreference: option },
      }).unwrap();

      await updateUserAssessment({
        id: user.id,
        userAssesment: { complete: true },
      }).unwrap();

      await refetchUser();

      track('ob_match_cont');

      if (option === 'email') {
        nextStep(TWO);
      } else {
        nextStep(ONE);
      }
    } catch (e) {
      captureException(e);
      setErrorMessage(
        'We couldn’t save your matching preference. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const onEmail = async () => {
    await onNext('email');
    track('ob_match_email');
  };
  const onCall = async () => {
    await onNext('call');
    track('ob_match_call');
  };

  return (
    <main className="bg-gradient-dark-sunny flex flex-1 flex-col overflow-auto">
      <div className="flex flex-col">
        <div className="container mx-auto p-4 text-center md:mt-4">
          <Typography className="mb-5" variant="h4">
            Tell us about&nbsp;
            <span className="bg-gradient-to-br from-poppy-500 to-bloom-500 bg-clip-text text-transparent">
              yourself
            </span>
          </Typography>
          <Typography className="mx-auto mb-8 max-w-140" variant="body1">
            We rely on the information you provide to connect you with great
            coaches.
          </Typography>
          <Typography className="mx-auto mb-6 max-w-100" variant="subtitle1">
            You will need to complete one of these options to start coaching.
          </Typography>
        </div>
        <div className="container mx-auto justify-center space-y-4 px-4 md:flex md:gap-4 md:space-y-0">
          <OptionCard
            buttonText="Start Reflection"
            disabled={saving}
            icon="mail"
            subtitle="Complete a 10-minute reflection to help us make a great match"
            title="Async Match"
            onClick={onEmail}
          />
          <OptionCard
            buttonText="Schedule Call"
            icon="videocam"
            subtitle="Schedule a 20-minute call to discuss your goals and coach options"
            title="Match Call"
            onClick={onCall}
          />
        </div>
        <Snackbar
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          autoHideDuration={7000}
          open={Boolean(errorMessage)}
          onClose={() => setErrorMessage(null)}
        >
          <Alert
            severity="error"
            style={{ width: '100%' }}
            onClose={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </div>
    </main>
  );
};

export default GetCoach;
